//* state.ts
//
// Stores in-memory context of the site page, allowing
// syncronization accross components.
//*/

import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import type { EmissionRecordsListRequest } from "../../../apiClient/generated";
import {
    type FilterState,
    filterStateToApiParams,
    type ProviderFilterState,
    type TableDateFilterState,
    type TableState,
    tableStateFamily,
} from "../../DataTable/state";

/**
 * Emission table state generator
 */

// Shared config id: filters/sorting/columns are shared across all sites; only
// pagination is scoped per-site (via the tableId). See tableStateFamily.
export const EMISSIONS_CONFIG_ID = "site-emissions";

// True only for a real, non-Invalid Date.
export const isValidDate = (d?: Date | null): d is Date =>
    d instanceof Date && !Number.isNaN(d.getTime());

// Day boundaries are UTC boundaries — the app renders in UTC throughout.
export const startOfUtcDay = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
// Inclusive end (not the next midnight): the backend range filter is gte/lte,
// and the DatePicker shows this value back to the user as a calendar date.
export const endOfUtcDay = (d: Date) =>
    new Date(
        Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            23,
            59,
            59,
            999,
        ),
    );

// Default date range bounds for a site (last 12 months).
export const defaultDateAfter = () =>
    startOfUtcDay(new Date(new Date().setMonth(new Date().getMonth() - 12)));
export const defaultDateBefore = () => endOfUtcDay(new Date());

// First-time defaults for a site's emissions table (localStorage overrides once
// present). The date range lives inside the filter state so it is the single
// page-wide source of truth, consumed by the DatePicker, SiteMap and Timeline.
const EMISSIONS_TABLE_INITIAL_STATE: Partial<TableState> = {
    sorting: [{ id: "detectionTimestamp", desc: true }],
    filters: {
        // Uses the backend filter name directly.
        detectionDateRange: {
            type: "date",
            after: defaultDateAfter(),
            before: defaultDateBefore(),
        },
        eventStatus: [
            "NEW",
            "CREATED",
            "COMPLETED",
            "WORK_IN_PROGRESS",
            "WAITING_APPROVAL",
            "LINKED",
        ],
    },
    columnVisibility: {
        concentration: false,
        responsible: false,
        createdAt: false,
        eventUpdatedAt: false,
        dataPointCreatedAt: false,
        location: false,
        bridgerEquipmentId: false,
        bridgerEquipmentType: false,
        bridgerEmissionLocationId: false,
        bridgerDetectionId: false,
        bridgerPersistence: false,
    },
};

// Factory for a site's emissions table atom. tableStateFamily is an atomFamily
// keyed on tableId only, so every caller (SiteView, EmissionsTable, the derived
// filter atoms) gets the SAME instance for a given siteId — routing all of them
// through this factory keeps the initialState identical and instanced once.
export const emissionsTableAtom = (siteId: string) =>
    tableStateFamily({
        tableId: `site-${siteId}-emissions`,
        configId: EMISSIONS_CONFIG_ID,
        initialState: EMISSIONS_TABLE_INITIAL_STATE,
    });

/**
 * Converts from table filter state to the EmissionFilters shape used by the map
 * and timeline. filterStateToApiParams JSON-stringifies providerWithSource (the
 * map hook stringifies again), so override it with the raw array/"all" value.
 */
export const tableStateToEmissionFilters = (
    tableFilters: FilterState,
): EmissionRecordsListRequest => {
    return {
        ...filterStateToApiParams(tableFilters),
        providerWithSource: (
            tableFilters.providerWithSource as ProviderFilterState
        )?.value,
    };
};

/**
 * Page-wide, read-only projection of a site's emissions table filter state,
 * keyed by siteId. This is the single source the Timeline subtree (and its
 * children) read for the date range + emission filters — no sync effect, no prop
 * drilling. Jotai caches the derived value, so the returned object is stable
 * until the underlying filters change.
 */
export const emissionPageStateFamily = atomFamily((siteId: string) =>
    atom((get) => {
        const filters = get(emissionsTableAtom(siteId)).filters;
        const dateRange = filters.detectionDateRange as
            | TableDateFilterState
            | undefined;
        // Snapped to whole days, so a same-day range is still a full day.
        // Fall back to the default range if either bound is missing/invalid,
        // so consumers never see undefined/Invalid Date.
        return {
            dateFrom: isValidDate(dateRange?.after)
                ? startOfUtcDay(dateRange.after)
                : defaultDateAfter(),
            dateTo: isValidDate(dateRange?.before)
                ? endOfUtcDay(dateRange.before)
                : defaultDateBefore(),
            emissionFilters: tableStateToEmissionFilters(filters),
        };
    }),
);

/**
 * Selection context
 *
 * Used to filter data visible from user interactions:
 *  - Picking a date range on timeline
 *  - Cliking on an emission
 */
interface SelectionContext {
    dateRange?: {
        start: Date;
        end: Date;
    };
    emissionId?: string;
    highlightPosition?: {
        date: Date;
        x: number;
    };
}
export const selectionContextAtom = atom<SelectionContext>({});

/**
 * Hover context
 *
 * Used to highlight information across the page when hovering over objects.
 *
 */
interface HoverContext {
    emissionIds?: string[];
    relatedPlumeImages?: string[];
}
export const hoverContextAtom = atom<HoverContext>();
